import { ref, reactive, computed } from 'vue'
import { defineStore } from 'pinia'
import API from '@util/API'
import { useToast } from './toast'

export const useIntegrations = defineStore('integrations', () => {
    const toast = useToast()

    const isPremium = window.formychat_admin_vars.is_premium == 1

    // State
    const state = reactive({
        isLoading: false,
        isSaving: false,
    })

    // Categories - will be populated from API
    const CATEGORY_LABELS = ref({
        all: 'All'
    })

    // Integration data - starts empty, populated from API
    const integrations = ref([])

    // Filter state
    const selectedCategory = ref('all')
    const searchQuery = ref('')

    // Computed categories from API response
    const categories = computed(() => {
        // Get all available categories from CATEGORY_LABELS (populated from API)
        const allCategoryKeys = Object.keys(CATEGORY_LABELS.value).filter(key => key !== 'all')
        // allCategoryKeys.sort()
        
        const categoryList = ['all', ...allCategoryKeys]
        
        return categoryList.map(slug => ({
            slug,
            label: CATEGORY_LABELS.value[slug] || slug
        }))
    })

    // Search/filter functionality
    const normalized = s => (s || '').toLowerCase().trim()
    const filteredIntegrations = computed(() => {
        if (!Array.isArray(integrations.value)) return []
        
        const s = normalized(searchQuery.value)
        const filtered = integrations.value.filter(i => {
            const categories = Array.isArray(i.categories) ? i.categories : []
            const in_category = selectedCategory.value === 'all' || categories.includes(selectedCategory.value)
            if (!in_category) return false

            if (!s) return true

            const haystack = [
                i.title || '',
                i.description || '',
                ...(Array.isArray(i.tags) ? i.tags : []),
                ...categories.map(c => CATEGORY_LABELS.value[c] || c)
            ].join(' ').toLowerCase()
            return haystack.includes(s)
        })
        
        return filtered
    })

    // Actions
    const toggleIntegration = async (integration) => {
        if (integration.status !== 'available') return

        integration.saving = true
        
        try {
            // Make request to API to toggle integration
            const response = await API.post(`integrations`, {
                integration_id: integration.id,
                enabled: integration.enabled
            })
            
            // Show success toast
            const integrationTitle = integration.title || integration.name || 'Integration'
            if (integration.enabled) {
                toast.show({
                    title: 'Enabled',
                    message: `${integrationTitle} module is turned on successfully!`,
                    type: 'success'
                })
            } else {
                toast.show({
                    title: 'Disabled', 
                    message: `${integrationTitle} module is turned off`,
                    type: 'disabled'
                })
            }
            
        } catch (error) {
            toast.error('Failed to toggle integration. Please try again.')
        } finally {
            integration.saving = false
        }
    }

    const openSettings = async (integration) => {
        if (!integration.enabled) return
        
        try {
            // Import modal store dynamically to avoid circular dependencies
            const { useIntegrationModal } = await import('./integrationModal')
            const integrationModal = useIntegrationModal()
            integrationModal.openModal(integration)
        } catch (error) {
            console.error('Error opening modal:', error)
        }
    }

    // API functions
    const loadIntegrations = async () => {
        state.isLoading = true
        try {
            const apiResponse = await API.get(`integrations`)
            const response = apiResponse.data


            if (response) {
                // Expected API response structure:

                // Update categories (includes translated 'all' and other category labels from API)
                if (response.categories) {
                    CATEGORY_LABELS.value = response.categories
                }

                // Transform integrations object to array
                if (response.integrations && typeof response.integrations === 'object') {
                    integrations.value = Object.values(response.integrations)
                } else {
                    integrations.value = []
                }

                // Check connection status for API integrations
                await checkMailchimpConnection()
                await checkGoogleSheetsConnection()
            }
        } catch (error) {
            console.error('Failed to load integrations:', error)
        } finally {
            state.isLoading = false
        }
    }

    const checkMailchimpConnection = async () => {
        try {
            const response = await API.get('integrations/get_mailchimp_key')
            const integration = integrations.value.find(i => i.id === 'mailchimp')
            if (integration) {
                integration.connection_status_checked = true
                if (response.data && response.data.api_key) {
                    integration.api_connected = true
                } else {
                    integration.api_connected = false
                }
            }
        } catch (error) {
            const integration = integrations.value.find(i => i.id === 'mailchimp')
            if (integration) {
                integration.connection_status_checked = true
                integration.api_connected = false
            }
        }
    }

    const checkGoogleSheetsConnection = async () => {
        try {
            const response = await API.get('integrations/google-sheets/status')
            const integration = integrations.value.find(i => i.id === 'google_sheets')
            if (integration) {
                integration.connection_status_checked = true
                if (response.data && response.data.connected) {
                    integration.api_connected = true
                } else {
                    integration.api_connected = false
                }
            }
        } catch (error) {
            const integration = integrations.value.find(i => i.id === 'google_sheets')
            if (integration) {
                integration.connection_status_checked = true
                integration.api_connected = false
            }
        }
    }

    const installIntegration = async (integration) => {
        
        // Determine action based on plugin state
        let action = 'install'
        if (integration.is_plugin_installed && !integration.is_plugin_active) {
            action = 'activate'
            integration.activating = true
        } else {
            integration.installing = true
        }

        try {
            const response = await API.post(`integrations/install`, {
                integration_id: integration.id,
                action: action
            })
            
            if ( response.data && response.data.success ) {
                integration.is_plugin_installed = true
                integration.is_plugin_active = true
                integration.enabled = isPremium

                // Show success toast
                toast.show({
                    title: response.data.action === 'activated' ? 'Activated' : 'Installed',
                    message: response.data.message || `${integration.title} module is ready!`,
                    type: 'success'
                })
            }
        } catch (error) {
            
            // Show error toast
            const errorMessage = error.response?.data?.message || 'Failed to install integration. Please try again.'
            toast.show({
                title: 'Installation Failed',
                message: errorMessage,
                type: 'error'
            })
        } finally {
            // Clear loading states
            integration.installing = false
            integration.activating = false
        }
    }

    const updateIntegrationConnectionStatus = (integrationId, isConnected) => {
        const integration = integrations.value.find(i => i.id === integrationId)
        if (integration) {
            integration.connection_status_checked = true
            integration.api_connected = isConnected
        }
    }

    return {
        // State
        state,
        integrations,
        selectedCategory,
        searchQuery,

        // Computed
        categories,
        filteredIntegrations,

        // Actions
        toggleIntegration,
        openSettings,
        loadIntegrations,
        updateIntegrationConnectionStatus,

        // Constants
        CATEGORY_LABELS,

        // API functions
        installIntegration,
    }
})